import { type CommonAction, response } from "@iflyrpa/share";
import { z } from "zod";
import { ActionCommonParamsSchema, AxiosProxyParams } from "../../types";
import { Http } from "../../utils/http";
import { DouyinGetHotParamsSchema } from "./schema";

const { getABogus } = require("../../utils/douyin/douyinSafe/a_bogus.js");
const { generateCsrfTokenAdvanced } = require("../../utils/douyin/douyinSafe/csrfToken.js");

export type DouyinGetHotParams = z.infer<typeof DouyinGetHotParamsSchema>;
export { DouyinGetHotParamsSchema } from "./schema";

export interface Hot {
    sentence_id: string;
    group_id: string;
    word: string;
    hot_value: number;
    word_cover: {
        url_list: string[];
    };
    video_count: number;
    word_type: number;
}

export interface DouyinHotResponse {
    status_code: number;
    status_msg: string;
    sentences?: Hot[];
    all_sentences?: Hot[];
    extra?: {
        logid: string;
        now: number;
    };
}

export type DouyinGetHotAction = CommonAction<
    DouyinGetHotParams,
    {
        hot: Hot[];
        total: number;
    }
>;

export const douyinGetHot: DouyinGetHotAction = async (_task, params) => {
    const userAgent =
        params.userAgent ||
        "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.38";

    const headers: any = {
        cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
        origin: "https://creator.douyin.com",
        referer: "https://creator.douyin.com/",
        Accept: "application/json, text/plain, */*",
        "Accept-Encoding": "gzip, deflate, br, zstd",
        "Content-Type": "application/json",
        pragma: "no-cache",
        "cache-control": "no-cache",
        "sec-ch-ua-platform": params.extraParam?.browserInfo?.["sec-ch-ua-platform"] || "",
        "sec-ch-ua": params.extraParam?.browserInfo?.["sec-ch-ua"] || "",
        "sec-ch-ua-mobile": "?0",
        "user-agent": userAgent,
        "sec-fetch-site": "same-origin",
        "sec-fetch-mode": "cors",
        "sec-fetch-dest": "empty",
        "accept-language": "zh-CN,zh;q=0.9",
        priority: "u=1, i",
    };
    const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.accountId];
    const http = new Http(...args);

    const csrfToken = generateCsrfTokenAdvanced({
        cookies: params.cookies,
        url: "https://creator.douyin.com/aweme/v1/hotspot/recommend/",
        method: "GET",
        userAgent,
    });

    const hotParams: {
        query?: string;
        count?: string;
        read_aid: string;
        aid: string;
        cookie_enabled: string;
        screen_width: string;
        screen_height: string;
        browser_language: string;
        browser_platform: string;
        browser_name: string;
        browser_version: string;
        browser_online: string;
        timezone_name: string;
        support_h265: string;
        msToken: string;
        a_bogus: string;
    } = {
        query: params.query || "",
        count: params.count?.toString() || "20",
        read_aid: "2906",
        aid: "1128",
        cookie_enabled: "true",
        screen_width: params.extraParam?.browserInfo?.screen_width || "1920",
        screen_height: params.extraParam?.browserInfo?.screen_height || "1080",
        browser_language: params.extraParam?.browserInfo?.browser_language || "zh-CN",
        browser_platform: params.extraParam?.browserInfo?.browser_platform || "Win32",
        browser_name: params.extraParam?.browserInfo?.browser_name || "Mozilla",
        browser_version: userAgent,
        browser_online: "true",
        timezone_name: "Asia/Shanghai",
        support_h265: "1",
        msToken: params.cookies.find((e) => e.name === "msToken")?.value || "",
        a_bogus: "",
    };
    const queryParamsBefore = new URLSearchParams(hotParams).toString();
    const aBogus = getABogus(
        `https://creator.douyin.com/aweme/v1/hotspot/${params.query ? "search" : "recommend"}/?${queryParamsBefore}`,
        {},
        userAgent,
    );
    hotParams.a_bogus = aBogus;
    if (!params.query) {
        delete hotParams.query;
        delete hotParams.count;
    }
    const queryString = new URLSearchParams(hotParams).toString();
    console.log("抖音获取热点参数:", queryString);
    const url = `https://creator.douyin.com/aweme/v1/hotspot/${params.query ? "search" : "recommend"}/?${queryString}`;
    console.log("抖音获取热点URL:", url);
    const res = await http.api<DouyinHotResponse>(
        {
            method: "get",
            url,
            headers: {
                ...headers,
                "X-secsdk-csrf-token": csrfToken,
            },
        },
        { retries: 3, retryDelay: 20, timeout: 3000 },
    );
    // console.log("抖音获取热点响应:", res?.sentences);

    const isSuccess = res.status_code === 0;
    const message = `抖音获取热点${isSuccess ? "成功" : `失败，原因：${res.status_msg}`}${_task.debug ? ` ${http.proxyInfo}` : ""}`;
    const data: { hot: Hot[]; total: number } = isSuccess
        ? {
              hot: res?.sentences || res?.all_sentences || [],
              total: (res?.sentences || res?.all_sentences)?.length || 0,
          }
        : { hot: [], total: 0 };
    console.log("抖音获取热点数据:", data);
    return response(isSuccess ? 0 : 414, message, data);
};
